import React, { useEffect, useState } from 'react';
import { settingsAPI } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import type { Settings } from '../../types';

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.settings);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('companyName', settings.companyName);
      formData.append('website', settings.website);
      formData.append('address', settings.address);
      if (settings.tagline) formData.append('tagline', settings.tagline);
      formData.append('primaryColor', settings.primaryColor);
      formData.append('secondaryColor', settings.secondaryColor);
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await settingsAPI.update(formData);
      setSettings(response.settings);
      setLogoFile(null);
      alert('Settings saved successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Configure your system settings</p>
      </div>

      {/* Company Information */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <Input
            label="Company Name"
            required
            value={settings.companyName}
            onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
          />

          <Input
            label="Website URL"
            type="url"
            required
            value={settings.website}
            onChange={(e) => setSettings({ ...settings, website: e.target.value })}
          />

          <Input
            label="Address"
            required
            value={settings.address}
            onChange={(e) => setSettings({ ...settings, address: e.target.value })}
          />

          <Input
            label="Tagline (optional)"
            value={settings.tagline || ''}
            onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Logo
            </label>
            {settings.logo && !logoFile && (
              <div className="mb-2">
                <img src={settings.logo} alt="Logo" className="h-16" />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                  className="h-10 w-20 border border-gray-300 rounded"
                />
                <Input
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                  className="h-10 w-20 border border-gray-300 rounded"
                />
                <Input
                  value={settings.secondaryColor}
                  onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                />
              </div>
            </div>
          </div>

          <Button type="submit" isLoading={saving}>
            Save Changes
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default SettingsPage;
